import React, { ReactNode, useEffect } from "react";
import { Platform } from "react-native";
import "./firebase"; // Ensure Firebase is initialized on web
import {
    getAuth,
    signInWithCustomToken,
    onAuthStateChanged,
    setLanguageCode,
} from "./FirebaseAuth";
import { useAtomValue } from "jotai";
import { currentLang } from "../atoms/i18n";

// used for testing to avoid uploading images
export const TEST_USER_ID = "xxx-test-user-id";

export const TEST_FIREBASE_USER: FirebaseUser = {
    uid: TEST_USER_ID,
};

/**
 * This is the exposed user object in the context
 */
export interface FirebaseUser {
    uid: string;
}

export enum FirebaseStatus {
    Loading,
    Out,
    In,
}

interface FirebaseContextInterface {
    status: FirebaseStatus;
    user?: FirebaseUser; // in replacement of FirebaseAuthTypes.User
    signOut: () => Promise<void>; // Function to sign out the user
}

export const FirebaseManagerContext = React.createContext<FirebaseContextInterface>({
    status: FirebaseStatus.Loading,
    user: undefined,
    signOut: () => Promise.resolve(), // Default no-op function
});

type FirebaseManagerProps = {
    children?: ReactNode | undefined;
    testContext?: FirebaseContextInterface; // Used for tests and storybooks
};

const signOut = async () => {
    const auth = getAuth();
    if (auth.currentUser) {
        return auth.signOut();
    }
    return Promise.resolve();
};

const FirebaseManager: React.FC<FirebaseManagerProps> = ({ children, testContext }) => {
    const [firebaseManagerContext, setFirebaseManagerContext] =
        React.useState<FirebaseContextInterface>(
            testContext
                ? testContext
                : {
                      status: FirebaseStatus.Loading,
                      signOut: signOut,
                  },
        );

    const lang = useAtomValue(currentLang);
    useEffect(() => {
        void setLanguageCode(getAuth(), lang || null);
    }, [lang]);

    React.useEffect(() => {
        // If testContext is provided, the initial `useState` already seeded the
        // context with it. Skip Firebase setup entirely in that case.
        if (testContext) {
            return;
        }
        if (Platform.OS === "web") {
            const url = new URL(window.location.href);
            const searchParams = url.searchParams;
            if (searchParams.has("admin_token")) {
                const loginAsAdmin = async () => {
                    const adminToken = searchParams.get("admin_token") as string;
                    await signOut();
                    await signInWithCustomToken(getAuth(), adminToken);
                    searchParams.delete("admin_token");
                    window.location.href = url.pathname + "?" + searchParams.toString();
                };
                void loginAsAdmin();
            }
        }
        return onAuthStateChanged(getAuth(), (user) => {
            if (user) {
                setFirebaseManagerContext({
                    status: FirebaseStatus.In,
                    user,
                    signOut: signOut,
                });
                return;
            }
            // User is signed out
            setFirebaseManagerContext({
                status: FirebaseStatus.Out,
                signOut: signOut,
            });
        });
    }, [testContext]);

    return (
        <FirebaseManagerContext.Provider value={firebaseManagerContext}>
            {children}
        </FirebaseManagerContext.Provider>
    );
};

export default FirebaseManager;
